import { useCallback, useEffect, useRef, useState } from 'react';
import sessionManager from 'utils/session';
import { SESSION_STATE, END_REASON } from 'utils/session/constants';

/** How often the countdown refreshes while the warning is on screen. */
const TICK_MS = 1000;

/**
 * Reads the session state from the one authority that owns it.
 *
 * Components observe; they never decide. A component that concludes on its own
 * that the session ended — because a query came back empty, say — is how this
 * app ended up with nine independent redirects to the login screen.
 *
 * @returns {{
 *   state: string,
 *   reason: string|null,
 *   isEnding: boolean,
 *   isIdleWarning: boolean,
 *   canExtend: boolean,
 *   msRemaining: number|null,
 *   extend: () => Promise<boolean>,
 *   stayActive: () => void,
 * }}
 */
export const useSession = () => {
  const [session, setSession] = useState(() => ({
    state: sessionManager.getState(),
    // Read rather than assumed: the manager may already be warning by the time
    // a component mounts, and starting at null would render the wrong dialog.
    reason: sessionManager.getReason(),
  }));
  const [msRemaining, setMsRemaining] = useState(() => sessionManager.timeToExpiry());

  useEffect(() => sessionManager.subscribe((state, reason) => {
    setSession({ state, reason: reason || null });
  }), []);

  // RENEWING counts as "ending" only if the user was already being warned.
  // An ordinary automatic renewal passes through RENEWING with nobody watching,
  // and treating that as a warning flashed the dialog open and shut on every
  // renewal cycle — a modal blinking at a user who had no problem to solve.
  const wasEndingRef = useRef(false);
  const isEnding =
    session.state === SESSION_STATE.EXPIRING ||
    (session.state === SESSION_STATE.RENEWING && wasEndingRef.current);

  useEffect(() => {
    wasEndingRef.current = isEnding;
  }, [isEnding]);

  // Two different clocks run out here, and the countdown has to show the one
  // that is actually about to fire.
  const isIdleWarning =
    session.state === SESSION_STATE.EXPIRING && session.reason === END_REASON.IDLE;

  const readRemaining = useCallback(
    () => (isIdleWarning ? sessionManager.timeToIdleClose() : sessionManager.timeToExpiry()),
    [isIdleWarning]
  );

  // The countdown only ticks while something is actually showing it. Running it
  // for the whole session would be a second timer competing with the manager's,
  // which is the accumulation this work exists to prevent.
  useEffect(() => {
    if (!isEnding) return undefined;

    setMsRemaining(readRemaining());
    const id = setInterval(() => setMsRemaining(readRemaining()), TICK_MS);
    return () => clearInterval(id);
  }, [isEnding, readRemaining]);

  const extend = useCallback(() => sessionManager.renewNow(), []);
  const stayActive = useCallback(() => sessionManager.stayActive(), []);

  return {
    ...session,
    isEnding,
    isRenewing: session.state === SESSION_STATE.RENEWING,
    isIdleWarning,
    canExtend: sessionManager.canExtend(),
    msRemaining,
    extend,
    stayActive,
  };
};

export default useSession;
